import type { FC, ReactNode } from 'react'
import type { SimFactory } from '@/types'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface SimCanvasProps {
    create: SimFactory
    label: string
    className?: string
}

// Runs a small 2D simulation on a canvas. It sizes itself to its box,
// renders at device resolution, only animates while it is on screen,
// and shows a single settled frame for visitors who prefer less motion.
export const SimCanvas: FC<SimCanvasProps> = ({ create, label, className }): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current

        if (!canvas) return

        const ctx = canvas.getContext('2d')

        if (!ctx) return

        const sim = create()
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        let width = 0
        let height = 0
        let frameId = 0
        let running = false
        let inView = false
        let last = 0

        const paint = () => {
            ctx.setTransform(1, 0, 0, 1, 0, 0)
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            const ratio = canvas.width / Math.max(width, 1)

            ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
            sim.draw(ctx, width, height)
        }

        const resize = () => {
            const rect = canvas.getBoundingClientRect()

            if (rect.width < 2 || rect.height < 2) return

            const ratio = Math.min(window.devicePixelRatio || 1, 2)

            width = rect.width
            height = rect.height
            canvas.width = Math.round(width * ratio)
            canvas.height = Math.round(height * ratio)
            sim.resize(width, height)
            paint()
        }

        const frame = (now: number) => {
            frameId = requestAnimationFrame(frame)

            const dt = Math.min((now - last) / 1000, 1 / 30)

            last = now
            sim.update(dt)
            paint()
        }

        const start = () => {
            if (running || reduceMotion) return

            running = true
            last = performance.now()
            frameId = requestAnimationFrame(frame)
        }

        const stop = () => {
            running = false
            cancelAnimationFrame(frameId)
        }

        resize()

        if (reduceMotion) {
            for (let index = 0; index < 360; index++) sim.update(1 / 60)
            paint()
        }

        const resizeObserver = new ResizeObserver(() => resize())

        resizeObserver.observe(canvas)

        const intersectionObserver = new IntersectionObserver(entries => {
            inView = entries.some(entry => entry.isIntersecting)

            if (inView && !document.hidden) start()
            else stop()
        }, { rootMargin: '120px 0px' })

        intersectionObserver.observe(canvas)

        const onVisibility = () => {
            if (document.hidden) stop()
            else if (inView) start()
        }

        document.addEventListener('visibilitychange', onVisibility)

        return () => {
            stop()
            resizeObserver.disconnect()
            intersectionObserver.disconnect()
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [create])

    return (
        <canvas
            ref={canvasRef}
            role='img'
            aria-label={label}
            className={cn('block size-full', className)}
        />
    )
}
